import JSZip from 'jszip';
import { LeadExportBundle } from '../../core/models/report.model';
import { toCsvBlob, triggerDownload } from './export.utils';

function extensionForMimeType(mimeType: string): string {
  const subtype = mimeType.split('/')[1] ?? 'bin';
  return subtype.split('+')[0]; // e.g. 'image/svg+xml' -> 'svg'
}

function dataUrlToBase64(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
}

/**
 * Bundles every filtered lead's flattened row (one `leads.csv`) plus its resolved photo
 * attachments into one zip -- "Lead export: full lead detail plus attachments for
 * QuoteIQ/spreadsheet handoff" (design doc §8), the piece phase 5 deferred to this phase
 * rather than building attachment-zip bundling twice. Only leads that actually have
 * attachments get a `photos/<lead>/` folder, so the archive isn't cluttered with empty
 * directories for the common case of a lead with no photos.
 */
export async function exportLeadsToZip(bundles: LeadExportBundle[], filename: string): Promise<void> {
  if (bundles.length === 0) {
    throw new Error('No data to export');
  }

  const zip = new JSZip();
  const csvBlob = toCsvBlob(bundles.map((b) => b.row));
  zip.file('leads.csv', await csvBlob.text());

  bundles.forEach((bundle, leadIndex) => {
    if (bundle.attachments.length === 0) return;
    const folderName = (bundle.lead.contact_name || bundle.lead.id).replace(/[^a-zA-Z0-9-_ ]/g, '_').trim();
    const folder = zip.folder(`photos/${folderName || `lead-${leadIndex + 1}`}`);
    bundle.attachments.forEach((attachment, photoIndex) => {
      const ext = extensionForMimeType(attachment.mime_type);
      folder?.file(`photo-${photoIndex + 1}.${ext}`, dataUrlToBase64(attachment.data_url), { base64: true });
    });
  });

  const blob = await zip.generateAsync({ type: 'blob' });
  triggerDownload(blob, `${filename}.zip`);
}
